/**
 * Single source of truth for "when is a petition debate live?".
 *
 * The Parliament API gives a date only ("2026-09-07"), never a time. Passing
 * that straight to new Date() yields midnight UTC, which is ~15.5 hours before
 * the debate actually starts — so a live window measured from it opened and
 * closed overnight and never covered the debate itself.
 */

// Westminster Hall e-petition debates start at 16:30 UK time on Mondays.
const DEBATE_START_HOUR = 16;
const DEBATE_START_MIN = 30;

// Westminster Hall petition debates run about 90 minutes. Two hours covers a
// normal overrun without leaving "Live Now" on air long after the room has
// emptied.
export const LIVE_WINDOW = 2 * 60 * 60 * 1000;

/**
 * Resolve a scheduled debate date to the moment it actually starts.
 * Handles GMT/BST without hardcoding an offset.
 */
export function getDebateTarget(dateStr) {
  if (!dateStr) return null;

  // An explicit timestamp is authoritative if Parliament ever starts sending one.
  if (String(dateStr).includes('T')) {
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const [year, month, day] = String(dateStr).split('-').map(Number);
  if (!year || !month || !day) return null;

  // Start from the intended wall-clock time as if UTC, then correct by however
  // much London differs at that instant (GMT in winter, BST in summer).
  let candidate = new Date(Date.UTC(year, month - 1, day, DEBATE_START_HOUR, DEBATE_START_MIN, 0));
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(candidate);

  const h = parseInt(parts.find(p => p.type === 'hour').value, 10);
  const m = parseInt(parts.find(p => p.type === 'minute').value, 10);
  const driftMins = (h * 60 + m) - (DEBATE_START_HOUR * 60 + DEBATE_START_MIN);
  if (driftMins !== 0) candidate = new Date(candidate.getTime() - driftMins * 60 * 1000);

  return candidate;
}

/** True while the debate is on air (start → start + LIVE_WINDOW). */
export function isDebateLive(dateStr, now = Date.now()) {
  const t = getDebateTarget(dateStr);
  if (!t) return false;
  const diff = t.getTime() - now;
  return diff <= 0 && diff > -LIVE_WINDOW;
}

/** True once the live window has fully elapsed. */
export function isDebatePast(dateStr, now = Date.now()) {
  const t = getDebateTarget(dateStr);
  if (!t) return false;
  return t.getTime() - now <= -LIVE_WINDOW;
}

// UK Parliament's YouTube channel. The live_stream endpoint resolves to
// whatever that channel is streaming right now and shows an "offline" card
// when nothing is — so there is no video ID to keep up to date.
export const UKP_CHANNEL_ID = 'UCMasyWuE1P2AaEKw_FkGq9g';
export const UKP_LIVE_EMBED = `https://www.youtube.com/embed/live_stream?channel=${UKP_CHANNEL_ID}`;
export const UKP_LIVE_URL = 'https://www.youtube.com/@ukparliament/live';
